/* eslint-disable no-cond-assign */
import classNames from 'classnames';
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { DataPoints, DataPointResponse } from '../DataPoints';

import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { LOCALE } from '../../../../utils/i18n';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { NotApplicableCell, PercentageCell } from '../../../../views/components/cells';
import { EQUITY_STYLE_CATEGORIES, CAPITALIZATION_CATEGORIES } from '../../../../utils/constants';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import DonutData from '@/types/donut-data';
import LocalizedString from '@/types/localized-string';
import ComparisonItem from '@/types/comparison-item';
import Dictionary from '@/types/dictionary';

/**
 * @class AssetAllocation Data Access Object
 */
export default class EquityStyleAllocationDAO extends ComparisonItemBasedProposalDAO {
  displayCategories: boolean = true;

  displayCapitalization: boolean = false;

  displayOther: boolean = true;

  displayNA: boolean = true;

  labelOverride?: Dictionary<LocalizedString>;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new EquityStyleAllocationDAO(proposal, proposalElement);
  }

  static EQUITY_STYLE_KEYS: string[] = [
    EQUITY_STYLE_CATEGORIES.VALUE,
    EQUITY_STYLE_CATEGORIES.BLEND,
    EQUITY_STYLE_CATEGORIES.GROWTH,
    EQUITY_STYLE_CATEGORIES.OTHER,
  ];

  static EQUITY_CAPITALIZATION_KEYS: string[] = [
    CAPITALIZATION_CATEGORIES.LARGE,
    CAPITALIZATION_CATEGORIES.MEDIUM,
    CAPITALIZATION_CATEGORIES.SMALL,
    CAPITALIZATION_CATEGORIES.OTHER,
  ];

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['EQUITY_STYLE_ALLOCATION'];
  }

  protected build(): void {
    // test for options

    const daoConfig: ProposalNS.EquityStyleAllocationConfig = this.proposalElement.DAO as ProposalNS.EquityStyleAllocationConfig;
    if (daoConfig.displayCategories !== undefined) {
      this.displayCategories = daoConfig.displayCategories;
    }
    if (daoConfig.displayCapitalization !== undefined) {
      this.displayCapitalization = daoConfig.displayCapitalization;
    }
    if (daoConfig.displayOther !== undefined) {
      this.displayOther = daoConfig.displayOther;
    }
    if (daoConfig.labelOverride !== undefined) {
      this.labelOverride = daoConfig.labelOverride;
    }

    if (daoConfig.displayNA !== undefined) {
      this.displayNA = daoConfig.displayNA;
    }

    const getLabelOverride = (key: string) => {
      const { locale } = this.proposal.options;

      return (this.labelOverride && this.labelOverride[key] && this.labelOverride[key][locale]) || this.formatMessage(key);
    };

    const styleStrings: string[] = [
      this.formatMessage('Proposal.Modules.EquityStyle.Value'),
      getLabelOverride('Proposal.Modules.EquityStyle.Blend'),
      this.formatMessage('Proposal.Modules.EquityStyle.Growth'),
      this.formatMessage('Common.Other'),
    ];
    const capitalizationStrings: string[] = [
      this.formatMessage('Proposal.Modules.EquityStyle.LargeCap'),
      this.formatMessage('Proposal.Modules.EquityStyle.MidCap'),
      this.formatMessage('Proposal.Modules.EquityStyle.SmallCap'),
      this.formatMessage('Common.Other'),
    ];

    let itemDAO = null;
    while ((itemDAO = this.nextItem()) !== null) {
      if (this.displayCategories) {
        this.addFieldDAO(itemDAO, new ProposalFieldDAO('', ''));
        this.addFieldName(
          this.formatMessage('Proposal.Modules.EquityStyle.Categories.EquityStyle'),
          classNames(ProposalStyleClassNames.ProposalTableGroup, ProposalStyleClassNames.ProposalTableContent),
        );

        const keys: LocalizedString[] = DataPoints.getEquityStyleKeys(itemDAO.item);
        this.processKeys(itemDAO, keys, styleStrings, EquityStyleAllocationDAO.EQUITY_STYLE_KEYS, DataPoints.getEquityStyleByKey);
      }

      if (this.displayCapitalization) {
        this.addFieldDAO(itemDAO, new ProposalFieldDAO('', ''));
        this.addFieldName(
          this.formatMessage('Proposal.Modules.EquityStyle.Categories.MarketCap'),
          classNames(ProposalStyleClassNames.ProposalTableGroup, ProposalStyleClassNames.ProposalTableContent),
        );

        const keys: LocalizedString[] = DataPoints.getEquityCapitalizationKeys(itemDAO.item);
        this.processKeys(itemDAO, keys, capitalizationStrings, EquityStyleAllocationDAO.EQUITY_CAPITALIZATION_KEYS, DataPoints.getEquityCapitalizationByKey);
      }
    }
  }

  private processKeys(
    itemDAO: ComparisonItemDAO,
    itemKeys: LocalizedString[],
    refKeyStrs: string[],
    refKeys: string[],
    dataPointsCall: (item: ComparisonItem, key: string) => DataPointResponse<number>,
  ) {
    for (let i = 0; i < refKeys.length; i++) {
      const refKey = refKeys[i];
      if (!this.displayOther && refKey === CAPITALIZATION_CATEGORIES.OTHER) {
        break;
      }

      const keyString: LocalizedString | undefined = itemKeys.find(key => refKey === key[LOCALE.EN_CA]);
      const key: string | undefined = keyString !== undefined ? keyString[LOCALE.EN_CA] : undefined;
      const color = (this.colorsDict && key && this.colorsDict[key]) || undefined;
      const fieldClasses = classNames(ProposalStyleClassNames.ProposalTableContent, ProposalStyleClassNames.ProposalTableIndentField);

      if (keyString !== undefined) {
        let value: number = 0;
        if (key !== undefined) {
          const dpResponse: DataPointResponse<number> = dataPointsCall(itemDAO.item, key);
          if (key.toUpperCase() === EQUITY_STYLE_CATEGORIES.OTHER.toUpperCase()) {
            this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER], itemDAO.item);
          }
          value = dpResponse.response;
          this.addDisclaimerKeys(dpResponse.disclaimerKeys, itemDAO.item);
          const field = new ProposalFieldDAO(refKeyStrs[i], value);
          field.key = key;
          field.cellType = PercentageCell;
          field.className = ProposalStyleClassNames.ProposalTableContent;

          this.addFieldDAO(itemDAO, field);
          this.addFieldName(refKeyStrs[i], fieldClasses, color);
        }
      } else if (this.displayNA) {
        const name = refKeyStrs[i];
        const field = new ProposalFieldDAO(name, '');
        field.cellType = NotApplicableCell;
        field.className = ProposalStyleClassNames.ProposalTableContent;
        this.addFieldDAO(itemDAO, field);
        this.addFieldName(name, fieldClasses, color);
      }
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (out && this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }

    return out;
  }
}
